import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation, Trans } from "react-i18next";
import { usePlayer } from "@/context/PlayerContext";
import { Compass, Sparkles, Shield, BookOpen, Users, Wrench, Brain, WifiOff, Heart, ShieldCheck, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SoundToggle from "@/components/SoundToggle";
import PreSignupSection from "@/components/PreSignupSection";

export default function Landing() {
  const { player } = usePlayer();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="relative min-h-screen hero-radial grain">
      {/* Twinkling stars */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(28)].map((_, i) => (
          <span
            key={i}
            className="twinkle absolute rounded-full bg-amber-100/80"
            style={{
              width: 2 + (i % 4),
              height: 2 + (i % 4),
              top: `${(i * 37) % 100}%`,
              left: `${(i * 53) % 100}%`,
              animationDelay: `${(i % 8) * 0.3}s`,
            }}
          />
        ))}
      </div>

      <header className="relative z-30 flex items-center justify-between px-6 sm:px-12 py-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-700 flex items-center justify-center shadow-lg shard-glow">
            <span className="font-serif font-black text-stone-950">C</span>
          </div>
          <div className="font-serif text-xl font-bold tracking-tight">Chronos <span className="text-amber-300">Time Puzzle</span></div>
        </div>
        <nav className="flex items-center gap-2 sm:gap-4 text-sm">
          <Link to="/about" className="hidden md:inline text-stone-300 hover:text-amber-200 transition-colors" data-testid="nav-about">{t("nav.about")}</Link>
          <Link to="/grimoire" className="hidden sm:inline text-stone-300 hover:text-amber-200 transition-colors" data-testid="nav-grimoire">{t("nav.grimoire")}</Link>
          <Link to="/parental" className="hidden sm:inline text-stone-300 hover:text-amber-200 transition-colors" data-testid="nav-parental">{t("nav.parents")}</Link>
          <SoundToggle />
          <LanguageSwitcher />
          {player && (
            <button
              onClick={() => navigate("/levels")}
              className="text-amber-300 font-bold hover:text-amber-200"
              data-testid="nav-continue"
            >
              {t("common.continue")} →
            </button>
          )}
        </nav>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 sm:px-12 pt-8 pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1 text-xs uppercase tracking-widest text-amber-200/90 font-bold mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            {t("landing.tag")}
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight text-stone-50">
            {t("landing.hero_line1")}<br/>
            <span className="text-amber-300 italic">{t("landing.hero_line2")}</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-stone-300 max-w-xl leading-relaxed">
            <Trans i18nKey="landing.hero_desc"
              components={{
                1: <span className="text-amber-200 font-serif italic" />,
                2: <span className="text-emerald-300" />,
                3: <span className="text-amber-300" />,
                4: <span className="text-teal-300" />,
              }}
            />
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button
              size="lg"
              onClick={() => navigate(player ? "/levels" : "/profile")}
              className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold text-base px-8 py-6 rounded-full shadow-xl shadow-amber-500/20"
              data-testid="cta-start"
            >
              <Compass className="w-5 h-5 mr-2" />
              {player ? t("landing.welcome_back", { name: player.name }) : t("landing.cta_start")}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate("/grimoire")}
              className="border-stone-500/50 bg-transparent text-stone-200 hover:bg-stone-800/40 rounded-full px-6 py-6"
              data-testid="cta-grimoire"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              {t("landing.cta_grimoire")}
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-stone-400" data-testid="hero-trust-badges">
            <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-400" /> {t("landing.badge_offline")}</div>
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-300" /> {t("landing.badge_modes")}</div>
            <div className="flex items-center gap-2"><EyeOff className="w-4 h-4 text-fuchsia-300" /> {t("landing.badge_no_ads")}</div>
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal-300" /> {t("landing.badge_gdpr")}</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="lg:col-span-5"
        >
          <div className="relative">
            {/* Three floating era cards */}
            <div className="grid gap-4">
              {[
                { key: "antiquity", labelKey: "landing.era_card_antiquity_label", tagKey: "landing.era_card_antiquity_tag", grad: "from-amber-700/70 to-orange-950/80", accent: "text-amber-200" },
                { key: "medieval",  labelKey: "landing.era_card_medieval_label",  tagKey: "landing.era_card_medieval_tag",  grad: "from-emerald-900/70 to-stone-950/80", accent: "text-emerald-200" },
                { key: "future",    labelKey: "landing.era_card_future_label",    tagKey: "landing.era_card_future_tag",    grad: "from-teal-800/70 to-cyan-950/80", accent: "text-teal-200" },
              ].map((era, i) => (
                <motion.div
                  key={era.key}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.15 }}
                  className={`glass rounded-2xl p-4 relative overflow-hidden`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${era.grad} opacity-70`} aria-hidden />
                  <div className="relative z-10">
                    <div className={`text-[10px] tracking-[0.3em] font-bold ${era.accent}`}>{t(era.labelKey)}</div>
                    <div className="mt-1 font-serif text-lg text-stone-100">{t(era.tagKey)}</div>
                  </div>
                </motion.div>
              ))}
            </div>
            {/* Vertical causality line */}
            <svg className="absolute left-6 top-0 h-full w-2 pointer-events-none" viewBox="0 0 10 300" preserveAspectRatio="none">
              <line x1="5" y1="10" x2="5" y2="290"
                stroke="url(#gcaus)" strokeWidth="2" strokeDasharray="4 6" className="causality-pulse" />
              <defs>
                <linearGradient id="gcaus" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#F59E0B" />
                  <stop offset="50%" stopColor="#4ade80" />
                  <stop offset="100%" stopColor="#14b8a6" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </motion.div>
      </main>

      {/* Trailer section */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 sm:px-12 py-10" data-testid="trailer-section">
        <div className="rounded-3xl glass-strong p-4 sm:p-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/25 via-transparent to-fuchsia-900/15" aria-hidden />
          <div className="relative z-10 grid lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <div className="rounded-2xl overflow-hidden aspect-video bg-stone-950 shadow-2xl shadow-amber-500/10 border border-white/10">
                <video
                  src="/trailer_hero.mp4"
                  poster="/trailer_poster.jpg"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  controls
                  className="w-full h-full object-cover"
                  data-testid="hero-trailer-video"
                />
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">{t("landing.trailer_kicker")}</div>
              <h3 className="mt-1 font-serif text-2xl sm:text-3xl font-black text-stone-50">
                {t("landing.trailer_title")}
              </h3>
              <p className="mt-3 text-sm text-stone-300 leading-relaxed">{t("landing.trailer_desc")}</p>
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="glass rounded-full px-3 py-1 text-amber-200">30s</span>
                <span className="glass rounded-full px-3 py-1 text-emerald-200">{t("landing.trailer_badge_offline")}</span>
                <span className="glass rounded-full px-3 py-1 text-fuchsia-200">{t("landing.trailer_badge_family")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketing sections */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 sm:px-12 py-16" data-testid="marketing-features">
        <div className="text-xs uppercase tracking-[0.3em] text-amber-300/90 font-bold text-center">{t("landing.features_kicker")}</div>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-black text-stone-50 text-center">{t("landing.features_title_1")}<span className="italic text-amber-300">{t("landing.features_title_2")}</span></h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {[
            { icon: WifiOff, title: t("landing.feature_offline_title"), text: t("landing.feature_offline_text") },
            { icon: Brain, title: t("landing.feature_cog_title"), text: t("landing.feature_cog_text") },
            { icon: Users, title: t("landing.feature_multi_title"), text: t("landing.feature_multi_text") },
            { icon: Heart, title: t("landing.feature_kind_title"), text: t("landing.feature_kind_text") },
          ].map((f, i) => (
            <motion.div key={f.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="glass rounded-2xl p-5"
            >
              <f.icon className="w-6 h-6 text-amber-300 mb-3" />
              <div className="font-serif text-lg font-bold text-stone-50">{f.title}</div>
              <p className="text-sm text-stone-300 mt-1 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="relative z-10 max-w-6xl mx-auto px-6 sm:px-12 py-16" data-testid="marketing-how">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-300/90 font-bold text-center">{t("landing.how_kicker")}</div>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-black text-stone-50 text-center">{t("landing.how_title_1")}<span className="italic text-emerald-300">{t("landing.how_title_2")}</span></h2>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {[
            { n: "01", t: t("landing.how_1_title"), d: t("landing.how_1_text") },
            { n: "02", t: t("landing.how_2_title"), d: t("landing.how_2_text") },
            { n: "03", t: t("landing.how_3_title"), d: t("landing.how_3_text") },
          ].map((s) => (
            <div key={s.n} className="glass rounded-3xl p-6">
              <div className="font-serif text-4xl font-black text-amber-300 mb-3">{s.n}</div>
              <div className="font-serif text-xl font-bold text-stone-50">{s.t}</div>
              <p className="text-sm text-stone-300 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative z-10 max-w-6xl mx-auto px-6 sm:px-12 py-16" data-testid="for-parents">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-300/90 font-bold text-center">{t("parents_section.kicker")}</div>
        <h3 className="mt-2 font-serif text-3xl sm:text-5xl font-black text-stone-50 text-center leading-tight">
          {t("parents_section.title_1")}<span className="italic text-emerald-300">{t("parents_section.title_2")}</span>
        </h3>
        <p className="mt-3 text-stone-300 text-center max-w-2xl mx-auto">{t("parents_section.subtitle")}</p>

        <div className="mt-10 grid sm:grid-cols-3 gap-5">
          {[
            { k: "cog",   value: t("parents_section.stat_1_v"), label: t("parents_section.stat_1_l"), color: "text-emerald-300" },
            { k: "safe",  value: t("parents_section.stat_2_v"), label: t("parents_section.stat_2_l"), color: "text-amber-300" },
            { k: "trust", value: t("parents_section.stat_3_v"), label: t("parents_section.stat_3_l"), color: "text-teal-300" },
          ].map((s) => (
            <div key={s.k} className="glass rounded-3xl p-6" data-testid={`parents-stat-${s.k}`}>
              <div className={`font-serif text-4xl font-black ${s.color}`}>{s.value}</div>
              <div className="mt-2 text-sm text-stone-300 leading-relaxed">{s.label}</div>
            </div>
          ))}
        </div>

        <ul className="mt-10 space-y-2 max-w-2xl mx-auto text-stone-200 text-sm">
          <li className="flex items-start gap-2"><span className="text-emerald-300 mt-0.5">✓</span> {t("parents_section.bullet_1")}</li>
          <li className="flex items-start gap-2"><span className="text-emerald-300 mt-0.5">✓</span> {t("parents_section.bullet_2")}</li>
          <li className="flex items-start gap-2"><span className="text-emerald-300 mt-0.5">✓</span> {t("parents_section.bullet_3")}</li>
          <li className="flex items-start gap-2"><span className="text-emerald-300 mt-0.5">✓</span> {t("parents_section.bullet_4")}</li>
        </ul>

        <div className="mt-8 text-center">
          <Button size="lg" onClick={() => navigate("/parental")} variant="outline" className="border-emerald-400/50 bg-transparent text-emerald-200 hover:bg-emerald-900/20 rounded-full px-8" data-testid="parents-cta">
            {t("parents_section.cta")}
          </Button>
        </div>
      </section>

      <section className="relative z-10 max-w-4xl mx-auto px-6 sm:px-12 py-16" data-testid="meet-the-maker">
        <div className="rounded-3xl glass-strong p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-stone-800/40 via-transparent to-amber-900/20" aria-hidden />
          <div className="relative z-10 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-4 flex justify-center">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-amber-400/20 to-stone-900 flex items-center justify-center border-4 border-amber-400/30 shadow-2xl shadow-amber-500/10">
                <span className="text-6xl" role="img" aria-label="Timekeeper's clock">🕰️</span>
              </div>
            </div>
            <div className="md:col-span-8">
              <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">{t("maker.kicker")}</div>
              <h3 className="mt-1 font-serif text-3xl sm:text-4xl font-black text-stone-50">{t("maker.title")}</h3>
              <p className="mt-4 text-stone-300 leading-relaxed">{t("maker.p1")}</p>
              <p className="mt-3 text-stone-300 leading-relaxed">{t("maker.p2")}</p>
              <p className="mt-5 text-amber-200 italic font-serif">— {t("maker.signature")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 max-w-3xl mx-auto px-6 sm:px-12 py-16" data-testid="faq-section">
        <div className="text-xs uppercase tracking-[0.3em] text-teal-300/90 font-bold text-center">{t("faq.kicker")}</div>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-black text-stone-50 text-center">{t("faq.title")}</h2>
        <div className="mt-10 space-y-3">
          {[1, 2, 3, 4, 5].map((n) => (
            <details key={n} className="glass rounded-2xl p-5 group" data-testid={`faq-item-${n}`}>
              <summary className="cursor-pointer font-serif text-base sm:text-lg text-stone-100 flex items-center justify-between gap-4 list-none">
                <span>{t(`faq.q${n}`)}</span>
                <span className="text-amber-300 text-xl transition-transform duration-200 group-open:rotate-45 shrink-0 select-none" aria-hidden>+</span>
              </summary>
              <p className="mt-3 text-sm text-stone-300 leading-relaxed">{t(`faq.a${n}`)}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="relative z-10 max-w-6xl mx-auto px-6 sm:px-12 py-16" data-testid="marketing-cta">
        <div className="rounded-3xl glass-strong p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/30 via-transparent to-teal-900/25" aria-hidden />
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">{t("landing.cta_kicker")}</div>
              <h3 className="mt-1 font-serif text-3xl sm:text-4xl font-black text-stone-50">{t("landing.cta_title")}</h3>
              <p className="mt-2 text-stone-300 max-w-xl">{t("landing.cta_desc")}</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button size="lg" onClick={() => navigate(player ? "/levels" : "/profile")} className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-8 py-6" data-testid="cta-start-bottom">
                <Compass className="w-5 h-5 mr-2" /> {t("landing.cta_start_bottom")}
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/editor")} className="border-stone-500/50 bg-transparent text-stone-200 rounded-full px-6 py-6" data-testid="cta-editor">
                <Wrench className="w-5 h-5 mr-2" /> {t("landing.cta_editor")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <PreSignupSection />

      <footer className="relative z-10 max-w-6xl mx-auto px-6 sm:px-12 py-8 text-center text-xs text-stone-500 border-t border-white/5 space-y-3">
        <div>{t("landing.footer")}</div>
        <div className="flex items-center justify-center pt-1" data-testid="footer-startupfame-badge">
          <a href="https://startupfa.me/s/chronostimepuzzle.com-640?utm_source=chronostimepuzzle.com" target="_blank" rel="noopener noreferrer">
            <img src="https://startupfa.me/badges/featured-badge.webp" alt="chronostimepuzzle - Featured on Startup Fame" width="171" height="54" />
          </a>
        </div>
        <nav className="flex items-center justify-center gap-4 flex-wrap" data-testid="footer-legal-nav">
          <Link to="/about" className="hover:text-amber-300 transition-colors" data-testid="footer-about">{t("nav.about")}</Link>
          <span aria-hidden>·</span>
          <Link to="/contact" className="hover:text-amber-300 transition-colors" data-testid="footer-contact">{t("nav.contact")}</Link>
          <span aria-hidden>·</span>
          <Link to="/press" className="hover:text-amber-300 transition-colors" data-testid="footer-press">{t("press.header")}</Link>
          <span aria-hidden>·</span>
          <Link to="/privacy" className="hover:text-amber-300 transition-colors" data-testid="footer-privacy">{t("legal.privacy_title")}</Link>
          <span aria-hidden>·</span>
          <Link to="/terms" className="hover:text-amber-300 transition-colors" data-testid="footer-terms">{t("legal.terms_title")}</Link>
          <span aria-hidden>·</span>
          <Link to="/legal-notice" className="hover:text-amber-300 transition-colors" data-testid="footer-notice">{t("legal.notice_title")}</Link>
        </nav>
      </footer>
    </div>
  );
}
